import { Dropdown, MenuProps } from 'antd';
import Icon, { DownOutlined } from '@ant-design/icons';
import { Icon as ArcSiteIcon } from '@arcsite/icons';
import { css } from '@emotion/react';
import { UserResponse } from '@/api';
import { useNavigate } from 'react-router-dom';
import { calculateRemainingTrialDays } from '@arcsite/utils';
import tracker from '@/tracker';
import { useAuth } from '@/contexts/AuthContext';
import { shouldFetchSubscription } from '@/pages/Subscribe/utils';
import { useBillingSubscription } from '@/pages/Subscribe/hooks';

// Import SVG icons
import { ReactComponent as help2 } from '@/assets/icons/help.svg';
import { ReactComponent as referralOrange } from '@/assets/icons/referral-orange.svg';
import { getEnterpriseHost } from '@/utils/env';

function ExpiredDes({
  expiredStatus,
  expiredDays,
}: {
  expiredDays?: string;
  expiredStatus: 'expired' | 'expire_soon' | 'not_expired_soon';
}) {
  let getExpiredDes = (): {
    className: string;
    description: string;
    brief: string;
  } => {
    switch (expiredStatus) {
      case 'not_expired_soon':
        return {
          className: 'bg-[#E37612]',
          description: `Trial - ${expiredDays} days left`,
          brief: 'Trial',
        };
      case 'expire_soon':
        return {
          className: 'bg-[#398AE7]',
          description: `Subscription will expire in ${expiredDays} days`,
          brief: '',
        };
      case 'expired':
        return {
          className: 'bg-[#6E6E6E]',
          description: `Subscription Expired`,
          brief: 'Expired',
        };
      default:
        return {
          className: 'bg-[#E37612]',
          description: `Trial - ${expiredDays} days left`,
          brief: 'Trial',
        };
    }
  };
  let { className, description, brief } = getExpiredDes();

  return (
    <div
      className={`${className} mx-auto text-white rounded px-1 py-0.5 mt-1 w-fit whitespace-nowrap`}
    >
      <span className="inline-block sm:hidden">{brief}</span>
      <span className="hidden sm:inline-block">{description}</span>
    </div>
  );
}

const UserName = ({ username }: { username: string }) => {
  return (
    <>
      <span className="hidden sm:inline-block">{username}</span>
      <span className="inline-block sm:hidden bg-[#E3571C] h-8 w-8 rounded-full text-white leading-8">
        {username.slice(0, 1).toUpperCase()}
      </span>
    </>
  );
};

// Create a union type to handle both User from Admin and UserResponse from Invite
type UserMenuUserType = Partial<UserResponse> & {
  username?: string;
  expired?: boolean;
  expired_without_extension?: boolean;
  default_org_purchase_type?: string;
  default_org_subs_expire_time?: string;
};

interface UserMenuProps {
  user: UserMenuUserType;
  onOpenDownloadModal: () => void;
  trackingSource?: string;
}

const UserMenu: React.FC<UserMenuProps> = ({
  user,
  onOpenDownloadModal,
  trackingSource = 'main_menu',
}) => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  // Use the billing subscription hook
  const { data: billingSubscription } = useBillingSubscription(
    user && shouldFetchSubscription(user as any)
  );

  const cancelled = billingSubscription?.current?.cancel_at != null;

  const expiredDays = user.default_org_subs_expire_time
    ? calculateRemainingTrialDays(user.default_org_subs_expire_time).toString()
    : '0';

  // Define menu items for the Dropdown component in Ant Design v5 format.
  // NOTE: It is crucial to attach `onClick` handlers directly to the menu items
  // instead of embedding them within the `label`'s JSX. Attaching onClick to
  // a child element (like a `div` inside `label`) can conflict with the
  // Dropdown's internal click handling, which may cause events to fire
  // unreliably or require a second click to trigger. Using the item's own
  // `onClick` prop ensures proper event registration and behavior.
  const menuStyles = {
    items: [
      {
        key: '-10',
        label: (
          <a
            href="https://affiliate.arcsite.com/"
            target="blank"
            className="flex items-center"
          >
            <Icon component={referralOrange} style={{ fontSize: 16 }} />
            <span className="ml-2.5 text-black-85">Become an affiliate</span>
          </a>
        ),
        className: 'text-16 text-[#E3571C] flex sm:!hidden',
        style: {
          height: 48,
          padding: '0 18px',
          borderBottom: '1px solid #ededed',
          fontWeight: 500,
        },
      },
      {
        key: '-9',
        label: (
          <div className="flex items-center">
            <Icon component={help2} style={{ fontSize: 16 }} />
            <span className="ml-2.5 text-black-85">Get Support</span>
          </div>
        ),
        onClick: () => {
          window.Intercom && window.Intercom('show');
        },
        className: 'text-24 flex sm:!hidden',
        style: {
          height: 48,
          padding: '0 18px',
          borderBottom: '1px solid #ededed',
          fontWeight: 500,
        },
      },
      {
        key: '0',
        label: (
          <a href={getEnterpriseHost()} target="_blank">
            <div className="flex items-center">
              <ArcSiteIcon
                name="advanced-settings-fill-24x24"
                className="text-[#5088e0]"
              />
              <span className="ml-2.5 text-black-85">
                Enterprise Admin Console
              </span>
            </div>
          </a>
        ),
        style: {
          height: 48,
          padding: '0 18px',
          borderBottom: '1px solid #ededed',
          fontWeight: 500,
        },
        hide: !user.is_enterprise_admin,
      },
      {
        key: '1',
        label: (
          <div className="flex items-center">
            <ArcSiteIcon name="star-fill-24x24" className="text-[#5088e0]" />
            <span className="ml-2.5 text-black-85">Subscription</span>
          </div>
        ),
        onClick: () => {
          tracker.logEvent('web_open_subscriptions', {
            from: trackingSource,
          });
          navigate('/app/subscribe');
        },
        style: {
          height: 48,
          padding: '0 18px',
          borderBottom: '1px solid #ededed',
          fontWeight: 500,
        },
      },
      {
        key: '2',
        label: (
          <div className="flex items-center">
            <ArcSiteIcon name="user-fill-24x24" className="text-[#5088e0]" />
            <span className="ml-2.5 text-black-85">Profile & Settings</span>
          </div>
        ),
        onClick: () => {
          tracker.logEvent('web_open_profile', {
            from: trackingSource,
          });
          navigate('/app/account-info');
        },
        style: {
          height: 48,
          padding: '0 18px',
          borderBottom: '1px solid #ededed',
          fontWeight: 500,
        },
      },
      {
        key: '3',
        label: (
          <div className="flex items-center">
            <ArcSiteIcon
              name="download-fill-24x24"
              className="text-[#5088e0]"
            />
            <span className="ml-2.5 text-black-85">Download App</span>
          </div>
        ),
        onClick: () => {
          tracker.logEvent('web_open_download_app', {
            from: trackingSource,
          });
          onOpenDownloadModal();
        },
        style: {
          height: 48,
          padding: '0 18px',
          borderBottom: '1px solid #ededed',
          fontWeight: 500,
        },
      },
      {
        key: '4',
        label: (
          <div className="flex items-center">
            <ArcSiteIcon name="logout-fill-24x24" className="text-[#5088e0]" />
            <span className="ml-2.5 text-black-85">Logout</span>
          </div>
        ),
        onClick: () => {
          return logout();
        },
        style: {
          height: 48,
          padding: '0 18px',
          borderBottom: '1px solid #ededed',
          fontWeight: 500,
        },
      },
    ].filter(item => !item.hide),
    style: {
      width: 256,
      background: '#F6FBFF',
      padding: 0,
      right: 0,
      position: 'absolute',
    },
    rootClassName: css`
      .ant-dropdown-menu-item {
        font-size: 13px;
      }
      .ant-dropdown-menu-item:hover {
        background-color: #e3eefe;
      }
    `.toString(),
  } as MenuProps;

  return (
    <Dropdown menu={menuStyles} placement={'bottomLeft'}>
      <div className="h-full flex items-center px-3 sm:px-4 relative border-l-1 border-black-10 hover:bg-gray-f5f5f5 mr-4 sm:mr-0 cursor-pointer">
        <a className="flex items-center self-stretch">
          <span className="inline-flex flex-col text-center items-center">
            <UserName username={user.username || ''} />
            {user.expired && <ExpiredDes expiredStatus="expired" />}
            {cancelled && (
              <ExpiredDes
                expiredStatus="expire_soon"
                expiredDays={expiredDays}
              />
            )}
            {user.default_org_purchase_type === 'TRIAL' &&
              !user.expired_without_extension && (
                <ExpiredDes
                  expiredDays={expiredDays}
                  expiredStatus="not_expired_soon"
                />
              )}
          </span>
          <DownOutlined className="ml-3" />
        </a>
      </div>
    </Dropdown>
  );
};

export default UserMenu;
